/**
 * 录入面板。
 *
 * 三种入口共用一套复核界面：因为它们产出的都是同一个 IntakeDraft，
 * 界面没有理由做三套。加第四种录入方式，这个文件也不用改。
 *
 * 三条不变式：
 *   1. 复核完成前不写库。识别错了改一改再提交，脏数据没机会进冰箱。
 *   2. 任何 AI 链路断了都落到手工模式，原文（转录/识别结果）留在顶部，
 *      用户不用重说一遍。
 *   3. 低置信度的条目会被标出来，并提示"这几项看一下"。
 */

import {
	FOOD_CATEGORIES,
	REVIEW_THRESHOLD,
	foodNameList,
	unitOptionsFor,
	type FoodCategory,
	type IntakeDraft,
	type IntakeItem,
} from '@fridge/shared';
import { useEffect, useRef, useState } from 'react';

import { ApiError, submitIntake } from '../api/client';
import { buildManualDraft } from '../intake/manual';
import { receiptAdapter, recognizeReceipt } from '../intake/receipt';
import { isAdapterAvailable, toManualFallbackDraft } from '../intake/types';
import {
	draftFromSpeechText,
	isSecureContextOk,
	isVoiceSupported,
	startVoiceSession,
	voiceAdapter,
} from '../intake/voice';
import { playAttention, playPop } from '../motion';
import { useAppData } from '../state/app-context';
import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';
import { Sheet } from './Sheet';
import { useToast } from './Toast';

const ALL_NAMES = foodNameList();

type BusyState = null | 'parsing' | 'submitting' | 'listening';

export interface IntakeSheetProps {
	open: boolean;
	onClose: () => void;
}

export function IntakeSheet({ open, onClose }: IntakeSheetProps): JSX.Element | null {
	const { caps, refreshFridge } = useAppData();
	const toast = useToast();

	const [draft, setDraft] = useState<IntakeDraft | null>(null);
	const [busy, setBusy] = useState<BusyState>(null);
	const [interim, setInterim] = useState('');
	const [voiceError, setVoiceError] = useState<string | null>(null);
	const reviewRef = useRef<HTMLDivElement | null>(null);

	// 关闭时清空，下次打开是干净的
	useEffect(() => {
		if (open) return;
		setDraft(null);
		setInterim('');
		setVoiceError(null);
		setBusy(null);
	}, [open]);

	// 拿到低置信度草稿时把视线引到复核区
	useEffect(() => {
		if (!draft) return;
		const needsReview = draft.items.some((item) => item.confidence < REVIEW_THRESHOLD);
		if (needsReview) playAttention(reviewRef.current);
		else playPop(reviewRef.current);
	}, [draft]);

	function ensureDraft(): IntakeDraft {
		return draft ?? buildManualDraft([]);
	}

	function blankItem(): IntakeItem {
		return {
			name: '',
			qty: null,
			unit: null,
			category: null,
			storedDays: 0,
			confidence: 1,
			raw: null,
		};
	}

	/**
	 * 进入手工录入。
	 *
	 * 必须**顺手给出一行空的**：只把草稿初始化成空数组的话，用户点完还是看到
	 * "还没有东西"，得再找一次"加一行"的按钮，白多一步。
	 * 已经有点东西了就不重复加。
	 */
	function startManual(): void {
		setDraft((previous) => {
			const base = previous ?? buildManualDraft([]);
			if (base.items.length > 0) return base;
			return { ...base, items: [blankItem()] };
		});
	}

	function patchItem(index: number, patch: Partial<IntakeItem>): void {
		setDraft((previous) => {
			const base = previous ?? buildManualDraft([]);
			return {
				...base,
				items: base.items.map((item, position) =>
					position === index ? { ...item, ...patch } : item,
				),
			};
		});
	}

	function removeItem(index: number): void {
		setDraft((previous) => {
			const base = previous ?? buildManualDraft([]);
			return { ...base, items: base.items.filter((_, position) => position !== index) };
		});
	}

	function addBlankRow(): void {
		setDraft((previous) => {
			const base = previous ?? buildManualDraft([]);
			return { ...base, items: [...base.items, blankItem()] };
		});
	}

	/* ---------------- 语音 ---------------- */

	function handleVoice(): void {
		if (busy) return;
		setVoiceError(null);
		setInterim('');

		if (!isVoiceSupported()) {
			setDraft(
				toManualFallbackDraft('voice', null, '这个浏览器不支持语音识别。', ensureDraft().items),
			);
			setVoiceError('这个浏览器不支持语音识别，已切到手工录入。');
			return;
		}
		if (!isSecureContextOk()) {
			setDraft(
				toManualFallbackDraft(
					'voice',
					null,
					'当前不是 HTTPS 环境，浏览器不允许开麦。',
					ensureDraft().items,
				),
			);
			setVoiceError('语音需要 HTTPS（localhost 除外），已切到手工录入。');
			return;
		}

		setBusy('listening');
		const session = startVoiceSession({
			onInterim: (text) => setInterim(text),
			onFinal: (text) => {
				setBusy('parsing');
				void (async () => {
					const next = await draftFromSpeechText(text);
					setDraft(mergeDraft(ensureDraft(), next));
					setBusy(null);
				})();
			},
			onError: (message) => {
				setBusy(null);
				setVoiceError(message);
			},
		});
		if (!session) setBusy(null);
	}

	/* ---------------- 拍照小票 ---------------- */

	async function handleReceipt(file: File): Promise<void> {
		setBusy('parsing');
		try {
			const next = await recognizeReceipt(file);
			setDraft(mergeDraft(ensureDraft(), next));
		} finally {
			setBusy(null);
		}
	}

	/* ---------------- 提交 ---------------- */

	async function handleSubmit(): Promise<void> {
		if (!draft) return;
		const items = draft.items.filter((item) => item.name.trim().length > 0);
		if (items.length === 0) {
			toast.show('还没填食材名呢。', 'warn');
			return;
		}

		setBusy('submitting');
		try {
			const result = await submitIntake(draft.source, items);
			const mergedNote = result.merged.length ? `，${result.merged.length} 样并入了已有条目` : '';
			toast.show(`入库 ${result.items.length} 项${mergedNote}。`, 'success');
			await refreshFridge();
			onClose();
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '入库失败', 'error');
		} finally {
			setBusy(null);
		}
	}

	const visionReady = isAdapterAvailable(receiptAdapter, caps);
	const items = draft?.items ?? [];
	const needsReview = items.filter((item) => item.confidence < REVIEW_THRESHOLD).length;

	return (
		<Sheet
			open={open}
			onClose={onClose}
			title="往冰箱里加东西"
			footer={
				<Pressable
					className="cta w-full"
					disabled={busy !== null || items.length === 0}
					onClick={() => void handleSubmit()}
				>
					{busy === 'submitting' ? '正在入库' : `确认入库${items.length > 0 ? `（${items.length}）` : ''}`}
				</Pressable>
			}
		>
			{/* 三种入口。不可用的仍然显示，但会说明原因：静默消失更让人困惑 */}
			<div className="grid grid-cols-3 gap-2">
				<AdapterTile
					glyph={GLYPHS.manual}
					label="手动添加"
					hint="永远可用"
					onClick={startManual}
				/>

				<label className={visionReady ? 'cursor-pointer' : 'pointer-events-none opacity-40'}>
					<AdapterTile
						glyph={GLYPHS.receipt}
						label="拍小票"
						hint={visionReady ? '识别商品行' : '未配视觉模型'}
						as="div"
						active={busy === 'parsing'}
					/>
					<input
						type="file"
						accept="image/*"
						capture="environment"
						className="hidden"
						disabled={!visionReady}
						onChange={(event) => {
							const file = event.target.files?.[0];
							event.target.value = '';
							if (file) void handleReceipt(file);
						}}
					/>
				</label>

				<AdapterTile
					glyph={GLYPHS.voice}
					label={busy === 'listening' ? '在听' : '说一句'}
					hint="短句最准"
					onClick={handleVoice}
					active={busy === 'listening'}
				/>
			</div>

			{interim ? (
				<div className="mt-3 rounded-input bg-accent-soft px-3 py-2 text-[13px] text-label">
					<span className="text-label-2">听到：</span>
					{interim}
				</div>
			) : null}

			{voiceError ? <p className="mt-2 text-[12px] text-warn">{voiceError}</p> : null}

			<div ref={reviewRef} className="mt-4">
				{items.length === 0 ? (
					<p className="py-8 text-center text-[13px] text-label-2">
						{busy === 'parsing' ? '正在识别' : '还没有东西。上面挑一种方式加进来。'}
					</p>
				) : (
					<>
						{draft?.transcript ? (
							<div className="mb-3 rounded-input bg-surface-2 px-3 py-2">
								<div className="mb-1 text-[11px] text-label-2">
									原文（{draft.source === 'voice' ? '语音转写' : '识别结果'}）
								</div>
								<p className="text-[13px] leading-relaxed text-label">{draft.transcript}</p>
							</div>
						) : null}

						{draft?.warnings.map((warning, index) => (
							<p
								key={index}
								className="mb-2 rounded-input bg-warn-soft px-3 py-2 text-[12px] leading-relaxed text-warn"
							>
								{warning}
							</p>
						))}

						{needsReview > 0 ? (
							<p className="mb-2 text-[12px] text-warn">
								有 {needsReview} 项把握不大，核对一下再入库。
							</p>
						) : null}

						<ul className="flex flex-col gap-2.5">
							{items.map((item, index) => (
								<DraftItemRow
									key={`${draft?.clientDraftId ?? 'local'}-${index}`}
									item={item}
									index={index}
									onPatch={patchItem}
									onRemove={removeItem}
								/>
							))}
						</ul>

						<Pressable
							className="cta-quiet mt-3 w-full py-2.5 text-[13px]"
							onClick={addBlankRow}
						>
							<IconGlyph glyph={GLYPHS.add} size={14} />
							再加一行
						</Pressable>
					</>
				)}
			</div>

			<datalist id="food-names">
				{ALL_NAMES.map((name) => (
					<option key={name} value={name} />
				))}
			</datalist>
		</Sheet>
	);
}

/* ------------------------------------------------------------------ */

function AdapterTile({
	glyph,
	label,
	hint,
	onClick,
	active = false,
	as = 'button',
}: {
	glyph: Parameters<typeof IconGlyph>[0]['glyph'];
	label: string;
	hint: string;
	onClick?: () => void;
	active?: boolean;
	as?: 'button' | 'div';
}): JSX.Element {
	const inner = (
		<>
			<IconGlyph
				glyph={glyph}
				size={22}
				weight={active ? 'fill' : 'regular'}
				className={active ? 'text-accent' : 'text-label'}
			/>
			<span className="text-[12.5px] font-medium text-label">{label}</span>
			<span className="text-[10.5px] leading-tight text-label-2">{hint}</span>
		</>
	);

	const className = `flex w-full flex-col items-center gap-1 rounded-card px-2 py-3 ${
		active ? 'bg-accent-soft' : 'bg-surface-2'
	}`;

	if (as === 'div') return <div className={className}>{inner}</div>;

	return (
		<Pressable className={className} onClick={onClick}>
			{inner}
		</Pressable>
	);
}

function DraftItemRow({
	item,
	index,
	onPatch,
	onRemove,
}: {
	item: IntakeItem;
	index: number;
	onPatch: (index: number, patch: Partial<IntakeItem>) => void;
	onRemove: (index: number) => void;
}): JSX.Element {
	const lowConfidence = item.confidence < REVIEW_THRESHOLD;
	const unitListId = `draft-units-${index}`;

	return (
		<li className={`rounded-card p-3 ${lowConfidence ? 'bg-warn-soft' : 'bg-surface-2'}`}>
			<div className="flex items-center gap-2">
				<input
					className="field flex-1"
					placeholder="食材名"
					list="food-names"
					value={item.name}
					onChange={(event) => onPatch(index, { name: event.target.value })}
				/>
				<Pressable
					aria-label="删掉这一项"
					scale={0.88}
					onClick={() => onRemove(index)}
					className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface text-label-2"
				>
					<IconGlyph glyph={GLYPHS.close} size={14} />
				</Pressable>
			</div>

			<div className="mt-2 flex items-center gap-2">
				<input
					className="field w-20 text-center"
					inputMode="decimal"
					placeholder="数量"
					value={item.qty ?? ''}
					onChange={(event) => {
						const raw = event.target.value.trim();
						onPatch(index, { qty: raw === '' ? null : Number.parseFloat(raw) });
					}}
				/>
				<input
					className="field w-20 text-center"
					list={unitListId}
					placeholder="单位"
					value={item.unit ?? ''}
					onChange={(event) => onPatch(index, { unit: event.target.value || null })}
				/>
				<datalist id={unitListId}>
					{unitOptionsFor(item.name).map((unit) => (
						<option key={unit} value={unit} />
					))}
				</datalist>

				<select
					className="field flex-1"
					value={item.category ?? '其他'}
					onChange={(event) => onPatch(index, { category: event.target.value as FoodCategory })}
				>
					{FOOD_CATEGORIES.map((category) => (
						<option key={category} value={category}>
							{category}
						</option>
					))}
				</select>
			</div>

			<div className="mt-2 flex items-center gap-2">
				<span className="shrink-0 text-[11px] text-label-2">已经放了</span>
				<input
					className="field w-16 text-center tnum"
					inputMode="numeric"
					placeholder="0"
					value={item.storedDays}
					onChange={(event) => {
						const raw = event.target.value.trim();
						onPatch(index, {
							storedDays: raw === '' ? 0 : Math.max(0, Number.parseInt(raw, 10) || 0),
						});
					}}
				/>
				<span className="text-[11px] text-label-2">天</span>
				<span className="ml-auto text-[11px] text-label-3">0 就是今天放的</span>
			</div>
		</li>
	);
}

/**
 * 把新拿到的草稿并进当前草稿：保留用户已经改过的内容，追加新的。
 * 场景：先说一句"还有鸡蛋"，再拍张小票，两者应该是叠加而不是覆盖。
 */
function mergeDraft(current: IntakeDraft, incoming: IntakeDraft): IntakeDraft {
	return {
		...incoming,
		clientDraftId: current.clientDraftId,
		items: [...current.items.filter((item) => item.name.trim()), ...incoming.items],
		transcript: [current.transcript, incoming.transcript].filter(Boolean).join('\n') || null,
	};
}
